import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { SignatureField, SignatureFieldHandle } from '@roc/ui';
import { format } from 'date-fns';
import { observer } from 'mobx-react';
import { NewLender } from '../../types/companyProfileTypes';


const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: 1000,
    margin: 'auto',
    padding: theme.spacing(6),
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
    overflow: 'auto',
    position: 'relative',
  },
  title: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  titleLeft: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginBottom: theme.spacing(4),
    textAlign: 'left',
  },
  paragraph: {
    fontSize: '0.7rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  sectionHeader: {
    fontWeight: 600,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 1300,
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

interface Props {
  signatureRef: React.RefObject<SignatureFieldHandle>;
  lender: NewLender;
  fromValues: any;
}

export const LoanAgreementContentPage = observer(({ signatureRef, lender, fromValues }: Props) => {
  const classes = useStyles();

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>
        MASTER LOAN PURCHASE AND SALE AGREEMENT
      </Typography>

      <Typography className={classes.paragraph}>
        This Master Loan Purchase and Sale Agreement (this <b>“Agreement”</b>),
        effective as of June 22, 2023 (the <b>“Effective Date”</b>), is entered
        into by and between [Seller] (the <b>“Seller”</b>), and Loan Funder LLC
        (together with its successors and assigns, the <b>“Buyer”</b>, and
        together with the Seller, each, a “Party” and collectively, the
        “Parties”).
      </Typography>

      <Typography className={classes.paragraph}>
        WHEREAS, the Seller (i) has, prior to the date hereof, sold to the Buyer
        certain first lien mortgage loans and related documents and payments as
        set forth on Schedule 1 hereto (“Previously Purchased Loans”); and (ii)
        owns and desire to sell certain first lien mortgage loans and related
        documents and payments as described herein (collectively with all
        Previously Purchased Loans, the “Purchased Loans” and each, a “Purchased
        Loan”) to the Buyer, and the Buyer is in the business of, among other
        things, purchasing first lien mortgage loans and desires to acquire the
        Purchased Loans from the Seller from time to time; and
      </Typography>

      <Typography className={classes.paragraph}>
        WHEREAS, the Purchased Loans will be sold by the Seller and purchased by
        the Buyer as pools or groups of whole loans, on a servicing released
        basis, on each Closing Date as provided herein
      </Typography>

      <Typography className={classes.paragraph}>
        NOW, THEREFORE, in consideration of the mutual promises and respective
        representations and warranties contained herein, and other good and
        valuable consideration, the receipt and sufficiency of which are hereby
        acknowledged, the Parties agree as follows:
      </Typography>

      <Typography className={classes.sectionHeader}>1. Definitions</Typography>
      <Typography className={classes.paragraph}>
        In addition to the terms defined elsewhere in this Agreement, the
        following definitions shall apply to this Agreement:
        <br />
        <br />
        <b>“Affiliate”</b> means, as applied to any Person, any other Person
        directly or indirectly controlling (including any member of the senior
        management group of such Person), controlled by, or under common control
        with, that Person. For the purposes of this definition, “control”
        (including, with correlative meanings, the terms “controlling,”
        “controlled by” and “under common control with”), as applied to any
        Person, means the possession, directly or indirectly, of the power (i)
        to vote 10% or more of the securities having ordinary voting power for
        the election of directors of such Person; or (ii) to direct or cause the
        direction of the management and policies of that Person, whether through
        the ownership of voting securities or by contract or otherwise.
        <br />
        <br />
        <b>“Applicable Law”</b> means all applicable (1) federal, state, and
        local legal requirements (including statutes, rules, regulations, and
        ordinances), including but not limited to, usury, equal credit
        opportunity, fair housing, privacy, disclosure or unfair and deceptive
        acts and practices laws, (2) requirements and guidelines of each
        governmental authority with authority to regulate banking, securities,
        or mortgage-related activities with respect to mortgage loans similar to
        the Mortgage Loans, and any similar agency or body, each to the extent
        of its authority over the affected Person or activity, and (3) judicial
        and administrative judgments, orders, stipulations, awards, writs, and
        injunctions, as such may be amended, modified or supplemented from time
        to time.
        <br />
        <br />
        <b>“Assignment and Conveyance”</b> means with respect to each Purchased
        Loan, the assignment and conveyance document, substantially in the form
        of Exhibit B attached hereto and incorporated by reference, confirming
        the sale by the Seller and the purchase by the Buyer of such Purchased
        Loan on the related Closing Date.
        <br />
        <br />
        <b>“Business Day”</b> means any day other than a Saturday, a Sunday or a
        day on which the New York Stock Exchange, the Federal Reserve or banking
        institutions in the State of New York are authorized or obligated by law
        or executive order to be closed. “Closing Date” means the date or dates,
        set forth in the related Assignment and Conveyance, on which the Buyer
        will purchase and the Seller will sell the Purchased Loans identified on
        the related Purchased Loan Schedule, attached as Annex A thereto and
        incorporated by reference.
        <br />
        <br />
        <b>“Confidential Information”</b> means all information, including but
        not limited to, records, documents, technology, software, trade secrets,
        financial and business information, loan information, and personally
        identifiable information of loan borrowers, guarantors, lenders,
        developers, or other related parties, whether oral or written, disclosed
        or made available to a Party or to which a Party is given access
        pursuant to, and inclusive of, this Agreement by the other Party.
        Confidential Information does not include any information which, at the
        time of disclosure, is: (i) generally known by the public; (ii) received
        from an unaffiliated third party without a breach by the receiving Party
        or such third party of any confidentiality obligation; (iii) information
        that has been independently developed by the receiving Party without use
        of, or reference to, the disclosing Party’s Confidential Information; or
        (iv) required to be disclosed by operation of law upon reasonable advice
        of counsel.
        <br />
        <br />
        <b>“Loan Documents”</b> means the original executed Note, allonge, along
        with all supporting loan documentation, including but not limited to,
        the Mortgage, guaranty, assignment of mortgage, the Participation
        Agreement (if any), the Environmental Indemnity Agreement and any other
        documents in the Seller’s loan file for each of the Purchased Loans
        acquired by the Buyer pursuant to this Agreement, including the
        documents set forth on Exhibit A hereto. “Loan Obligor(s)” means each
        and every person or entity obligated to comply with the terms and
        conditions of the related Note and other Loan Documents for each
        Purchased Loan under this Agreement.
        <br />
        <br />
        <b>“Mortgage”</b> means a mortgage, deed of trust, deed to secure debt
        or other instrument, creating a valid and enforceable first lien on or a
        first priority ownership interest in an estate in fee simple in
        Mortgaged Property and the improvements thereon, securing a Note or
        similar evidence of indebtedness.
        <br />
        <br />
        <b>“Mortgaged Property”</b> means the fee simple interest in a single
        parcel of real property improved by a residential dwelling.
        <br />
        <br />
        <b>“Note”</b> means a promissory note or other evidence of indebtedness
        of a Loan Obligor secured by a Mortgage and evidencing a Purchased Loan.
        <br />
        <br />
        <b>“Participation Agreement”</b> means those participation agreements,
        if any, relating to a Purchased Loan.
        <br />
        <br />
        <b>“Participation Interest”</b> means a synthetic subordinated
        participation interest in a Purchased Loan.
        <br />
        <br />
        <b>“Permitted Encumbrances”</b> means (a) the lien of current real
        property taxes and assessments not yet delinquent; (b) covenants,
        conditions and restrictions, rights of way, easements and other matters
        of the public record as of the date of recording acceptable to prudent
        mortgage lending institutions generally and specifically referred to in
        the lender’s title insurance policy delivered to the originator of a
        Purchased Loan; and (c) other matters to which like properties are
        commonly subject which do not materially and adversely interfere with
        the benefits of the security intended to be provided by the Mortgage or
        the use, enjoyment, value or marketability of the related Mortgaged
        Property.
        <br />
        <br />
        <b>“Person”</b> means any individual, corporation, partnership, limited
        liability company, joint venture, association, joint stock company,
        trust (including any beneficiary thereof), unincorporated organization
        or government or any agency or political subdivision thereof.
        <br />
        <br />
        <b>“Representative”</b> means any Affiliate and any subsidiary, officer,
        manager, director, member, owner, employee, agent, or representative of
        the Buyer or the Seller, respectively
      </Typography>

      <Typography className={classes.sectionHeader}>
        2. Rules of Construction
      </Typography>
      <Typography className={classes.paragraph}>
        For all purposes of this Agreement, except as otherwise expressly
        provided or unless the context otherwise requires, (i) singular words
        shall connote the plural as well as the singular, and vice versa (except
        as indicated), as may be appropriate; (ii) the words “herein,” “hereof”
        and “hereunder” and other words of similar import used in this Agreement
        refer to this Agreement as a whole and not to any particular appendix,
        article, schedule, section, paragraph, clause, exhibit or other
        subdivision; (iii) the masculine shall include the feminine and the
        plural shall include the singular and vice versa; (iv) the headings and
        subheadings set forth in this Agreement are solely for convenience of
        reference and shall not constitute a part of this Agreement nor shall
        they affect the meaning, construction or effect of any provision hereof;
        (v) references in this Agreement to “including” shall mean including
        without limiting the generality of any description preceding such term,
        and for purposes hereof the rule of ejusdem generis shall not be
        applicable to limit a general statement, followed by or referable to an
        enumeration of specific matters, to matters similar to those
        specifically mentioned; (vi) each of the Parties to this Agreement and
        its counsel have reviewed and revised, or requested revisions to, this
        Agreement, and the usual rule of construction that any ambiguities are
        to be resolved against the drafting party shall be inapplicable in the
        construction and interpretation of this Agreement; (vii) any definition
        of or reference to any agreement, instrument or other document shall be
        construed as referring to such agreement, instrument or other document
        as from time to time amended, supplemented or otherwise modified
        (subject to any restrictions on such amendments, supplements or
        modifications set forth herein or in any other Loan Document); and
        (viii) any reference to any law shall include all statutory and
        regulatory provisions consolidating, amending, replacing or interpreting
        such law and any reference to any law or regulation shall, unless
        otherwise specified, refer to such law or regulation as amended,
        modified or supplemented from time to time.
      </Typography>

      <Typography className={classes.sectionHeader}>
        3. Sale and Purchase
      </Typography>
      <Typography className={classes.paragraph}>
        In exchange for the Buyer’s funding of the related loan amount(s)
        (pursuant to the related Loan Documents), on the origination date of
        certain Purchased Loan(s) as indicated in the related Assignment and
        Conveyance, the Seller agrees to sell and the Buyer agrees to purchase,
        all of the Seller’s rights, title and interest in and to such Purchased
        Loan(s), including but not limited to, all interest and principal
        payments and other amounts due or to become due thereunder, subject to
        any Participation Interests in the Purchased Loans. The acquisition of
        the Purchased Loans under this Agreement shall be on a servicing
        released basis.<br />
        <br /> The Buyer shall be entitled to receive with respect to a
        Purchased Loan purchased hereunder, (i) all payments of interest
        collected on or after the related Closing Date; and (ii) all other
        payments applicable to the Purchased Loan collected on and after the
        related Closing Date.<br />
        <br /> All Loan Documents shall be delivered by the
        Seller to the Buyer or its designee on or before the related Closing
        Date.<br />
        <br /> The Seller hereby assigns all of its rights, title, and interest
        in and to each of the Loan Documents with respect to each Purchased Loan
        to the Buyer.
      </Typography>

      <Typography className={classes.sectionHeader}>
        4. Assignment and Conveyance
      </Typography>
      <Typography className={classes.paragraph}>
        With respect to each Purchased Loan acquired by Buyer from Seller
        pursuant to this Agreement, the Seller shall execute and deliver to the
        Buyer an Assignment and Conveyance; provided, that the Seller and the
        Buyer each hereby agree and acknowledge that the Seller did not provide
        an Assignment and Conveyance in connection with any Previously Purchased
        Loans.
      </Typography>

      <Typography className={classes.sectionHeader}>
        5. Assignment and Delivery of Purchased Loans
      </Typography>
      <Typography className={classes.paragraph}>
        As instructed, the Seller shall take, or cause to be taken, actions so
        as to effectuate the transfer of the Seller’s rights, title and interest
        in the Purchased Loans to the Buyer. The Seller shall execute and
        acknowledge (or cause to be executed and acknowledged) and deliver to
        the Buyer all documents, and take all actions, reasonably required by
        the Buyer from time to time to confirm the rights created under this
        Agreement, to protect and further the validity, priority and
        enforceability of the Loan Documents, including without limitation,
        executing any revised mortgage assignments or allonges, or otherwise to
        carry out the purposes of this Agreement and the transactions
        contemplated hereunder
      </Typography>

      <Typography className={classes.sectionHeader}>
        6. Seller’s Representations and Warranties Regarding the Purchased
        Loans.
      </Typography>
      <Typography className={classes.paragraph}>
        The Seller represents and warrants to the Buyer, as of the related
        Closing Date and with respect to each Purchased Loan:
      </Typography>

      <Box pl={3}>
        <Typography className={classes.paragraph}>
          <b>a - Purchased Loans.</b> The information pertaining to the
          Purchased Loan as provided to the Buyer is true and correct in all
          material respects.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>b - Consumer Laws.</b> The Purchased Loan was not originated for
          primarily consumer, family, or household purposes. The related
          Mortgaged Property securing the Mortgage is non-owner occupied.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>c - Origination and Underwriting; Compliance with Law.</b> The
          Seller underwrote the Purchased Loan at origination and in accordance
          with the Buyer’s underwriting guidelines. The origination practices of
          the Seller with respect to the Purchased Loan have been, in all
          respects, legal, and as of the date of its origination, such Purchased
          Loan and the origination thereof complied in all respects with, or was
          exempt from, all requirements of federal, state, or local law relating
          to the origination of such Purchased Loan. For the avoidance of doubt,
          the Seller has complied with all Applicable Laws (including all
          related reporting requirements) with respect to the Purchased Loan,
          including without limitation, (i) the Mortgage Disclosure Act and
          Regulation C issued thereunder; (ii) the Equal Credit Opportunity Act
          (“ECOA”) and Regulation B issued thereunder; (iii) Section 5 of the
          Federal Trade Commission Act regarding unfair or deceptive acts or
          practices; (iv) the Fair Credit Reporting Act and Regulation V issued
          thereunder; and (v) state and local law equivalents of all of the
          foregoing.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>d - Condemnation.</b> There is no proceeding pending or threatened
          for the total or partial condemnation of the related Mortgaged
          Property or the taking of such Mortgaged Property by eminent domain.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>e - Ownership of Purchased Loan.</b> The Seller has good title to,
          and was the sole legal, beneficial and equitable owner of, the
          Purchased Loan free and clear of any and all liens, charges, pledges,
          encumbrances, security interests, participations, any other ownership
          interests on, in or to such Purchased Loan, except for Permitted
          Encumbrances and any Participation Interest in the Purchased Loan. The
          Seller has the full right and authority to sell, assign, and transfer
          the Purchased Loan subject to any Participation Interest therein, and
          the assignment of the Purchased Loan to the Buyer constitutes a legal,
          valid, and binding assignment of such Purchased Loan free and clear of
          any and all liens, pledges, charges, or security interests of any
          nature encumbering such Purchased Loan subject to any Permitted
          Encumbrances and any Participation Interest in the Purchased Loan.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>f - Loan Document Status.</b> The related Note and Mortgage, and
          each other loan document, are the legal, valid, and binding
          obligations of the related Loan Obligor, and are each enforceable in
          accordance with its respective terms.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>g - Mortgage Status; Waivers.</b> Since the origination date of the
          Purchased Loan (i) the terms of the Mortgage, Note, guaranty, and
          related Loan Documents with respect to such Purchased Loan have not
          been waived, impaired, modified, altered, satisfied, canceled,
          subordinated, or rescinded in any respect; (ii) no Mortgaged Property
          securing such Purchased Loan or any portion thereof has been released
          from the lien of the Mortgage securing such Purchased Loan in any
          manner which interferes with the security intended to be provided by
          such Mortgage or the use or operation of the remaining portion of such
          Mortgaged Property; and (iii) neither the Loan Obligor or guarantor of
          such Purchased Loan has been released from its obligations under such
          Purchased Loan.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>h - Lien; Valid Assignment.</b> The assignment of the related
          Mortgage constitutes a legal, valid, and binding assignment of the
          Mortgage purported to be signed thereby. The Mortgage is freely
          assignable without the consent of the related Loan Obligor but subject
          to the terms of any related Participation Interest. The Mortgage is a
          legal, valid and enforceable first lien on the related Loan Obligor’s
          fee interest in the Mortgaged Property encumbered by such Mortgage to
          the extent of the principal amount of the Purchased Loan secured by
          such Mortgage or the allocated loan amount (subject only to Permitted
          Encumbrances and any exceptions to title coverage set forth in the
          related lender’s title insurance policy). Such Mortgaged Property
          (subject to and excepting Permitted Encumbrances and any exceptions to
          title coverage set forth in the related lender’s title insurance
          policy) as of the Purchased Loan’s origination date was, and as of the
          related Closing Date is, free and clear of any recorded mechanics’
          liens, recorded materialmen’s liens, and other recorded encumbrances
          which are prior to or equal with the lien of the Mortgage encumbering
          such Mortgage Property, except those which are bonded over, escrowed
          for or insured against by a lender’s title insurance policy.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>i - Environmental Conditions.</b> (i) At the time of origination,
          the related Mortgaged Property was in compliance with all then
          applicable environmental laws pertaining to environmental hazards
          (including, without limitation, asbestos); and (ii) there is no
          pending actions or proceedings directly involving any Mortgaged
          Property in which compliance with any environmental law, rule, or
          regulation is at issue.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>j - Property Condition.</b> Except as disclosed in the related
          appraisal or any related construction or property rehabilitation plan
          disclosed to the Buyer in writing, the Mortgaged Property is undamaged
          by waste, fire, earthquake or earth movement, windstorm, hurricane,
          flood, tornado or other casualty so as to materially and adversely
          affect the value of the Mortgaged Property as security for the
          Mortgage Loan. The related Mortgaged Property is in substantially the
          same condition it was in at the time the most recent appraisal was
          obtained.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>k - No Mechanics’ Liens.</b> There are no mechanics’ or similar
          liens or claims which have been filed, or threatened to be filed, for
          work, labor or material (and no rights are outstanding that under the
          law could give rise to such liens or claims) affecting the related
          Mortgaged Property which are or may be liens prior to, or equal or
          coordinate with, the lien of the related Mortgage.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>l - Compliance with Usury Laws.</b> The mortgage interest rate
          (exclusive of any default interest, late charges, yield maintenance
          charge, or prepayment premiums) of the Purchased Loan complied as of
          the date of origination with, or was exempt from, applicable state or
          federal laws, regulations and other requirements pertaining to usury.
          The Purchased Loan is not subject to forfeiture or any material
          penalties as a result of non-compliance with any applicable state or
          federal laws, regulations and other requirements pertaining to usury.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>m - No Material Default.</b> There is (i) no material default,
          breach, violation, or event of acceleration existing under any
          Purchased Loan; or (ii) no event (other than payments due but not yet
          delinquent) which, with the passage of time or with notice and the
          expiration of any grace or cure period, would constitute a material
          default, breach, violation or event of acceleration.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>n - Bankruptcy.</b> As of the date of origination of the Purchased
          Loan and as of the related Closing Date, neither the related Loan
          Obligor nor guarantor was a debtor in state or federal bankruptcy,
          insolvency or similar proceeding.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>o - Assignment of Collateral.</b> There is no material collateral
          securing the Purchased Loan that has not been sold, pledged, or
          assigned to the Buyer.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>p - No Encroachments.</b> Except for encroachments that (i) do not
          materially and adversely affect the current marketability or principal
          use of the Mortgaged Property (beyond any affects taken into account
          at the time of the date of origination of the Purchased Loan); (ii)
          are intended to be remedied by the related Loan Obligor as part of its
          improvement or rehabilitation of the Mortgaged Property; or (iii) are
          insured against by the applicable lender’s title insurance policy, all
          material improvements that were included for the purpose of
          determining the value of the related Mortgaged Property at the time of
          the origination of such Purchased Loan are within the boundaries of
          the related Mortgaged Property.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>q - Loan Documents.</b> As of the related Closing Date, each Loan
          Document has been delivered to the Buyer or its designee.
        </Typography>
      </Box>

      <Typography className={classes.sectionHeader}>
        7. Seller’s Representations and Warranties.
      </Typography>
      <Typography className={classes.paragraph}>
        The Seller represents and warrants to the Buyer, as of the date of this
        Agreement and each related Closing Date, that:
      </Typography>
      <Box pl={3}>
        <Typography className={classes.paragraph}>
          <b>a - Organization; Powers.</b> The Seller is duly organized, validly
          existing, and in good standing under the laws of its state of
          formation, and has full power, right, and authority to enter into this
          Agreement, to sell the Purchased Loans hereunder and to perform each
          and all of the matters and things herein provided.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>b - Seller Authority, Etc.</b> The execution, delivery, and
          performance by the Seller of this Agreement and the transactions
          contemplated hereby have been duly authorized by all necessary
          corporate action and, assuming valid execution by the Buyer, this
          Agreement constitutes the legal, valid, and binding obligations of the
          Seller enforceable against the Seller in accordance with its terms,
          except to the extent that enforceability may be limited by bankruptcy,
          insolvency, reorganization or other laws affecting creditors’ rights
          generally and general principles of equity, regardless of whether such
          enforceability is considered in a proceeding in equity or law.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>c - Compliance with Laws and Contracts.</b> The execution,
          delivery, and performance by the Seller of this Agreement does not and
          will not (i) violate any provision of any law, rule, regulation,
          order, writ, judgment, injunction, decree, determination or award to
          which the Seller or its property is subject, or of the organizational
          documents of the Seller; (ii) result in a breach of or constitute a
          default under the provisions of any indenture, loan, or credit
          agreement or any other material agreement, lease or instrument to
          which the Seller may be or is subject or by which it, or its property,
          is bound; or (iii) result in, or require, the creation or imposition
          of any lien on or with respect of any Purchased Loans, except as
          contemplated by this Agreement. The Seller is not in violation of, or
          in default under, any such law, rule, regulation, order, writ,
          judgment, injunction, decree, determination, or award or any such
          indenture, agreement, lease or instrument. The Seller is in compliance
          with all Applicable Law, including being duly licensed to originate
          and sell Purchased Loans in each state or jurisdiction in which such
          license is required.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>d - Governmental Approvals.</b> The Seller has obtained all
          required authorizations, consents, approvals, licenses, exemptions of
          or filings or registrations with all governmental commissions,
          regulatory bodies, boards, bureaus, agencies and instrumentalities,
          domestic or foreign, necessary to the valid execution, delivery and
          performance by the Seller of this Agreement (the “Seller Approvals”),
          and such Seller Approvals remain in full force and effect.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>e - Litigation.</b> There is no action, suit, proceeding, inquiry
          or investigation at law or in equity or before or by any court, public
          board or body pending or, threatened in writing against or affecting
          the Seller wherein an unfavorable decision, ruling or finding would
          have a material adverse effect, or purports to have a material adverse
          effect, on the validity or enforceability against the Seller of this
          Agreement.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>f - Perfected Interest.</b> Immediately prior to the sale thereof
          by the Seller to the Buyer hereunder, each Purchased Loan sold to the
          Buyer hereunder and the assets related thereto, is owned by the
          Seller, free and clear of any adverse claim, judgment or lien other
          than any lien created hereby. No further action, including any filing
          or recording of any document, is necessary in order to establish,
          protect and perfect the interest of the Buyer, as a first priority
          ownership interest in any applicable jurisdiction, as against any
          purchaser from, or creditor of, the Seller.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>g - Solvency.</b> The sale of the Purchased Loans is not undertaken
          to hinder, delay or defraud any of the creditors of the Seller and is
          not made in contemplation of any insolvency proceeding of the Seller.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>h - Title to Purchased Loans.</b> (i) The transfer of the Purchased
          Loans constitutes the absolute transfer of all right, title and
          interest of the Seller in such Purchased Loans to the Buyer, free and
          clear of any lien or other adverse claim other than any Participation
          Interest in the Purchased Loans; and (ii) the Seller has not taken any
          action to cause any such Purchased Loans to become the subject of any
          lien or adverse claims against the Seller, other than those which have
          been released on or prior to the date hereof, or are in favor of the
          Buyer pursuant to this Agreement other than any Participation
          Interests in the Purchased Loans.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>i - Ordinary Course of Business.</b> The sale of the Purchased
          Loans hereunder is in the ordinary course of business for the Seller.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>j - Data Security Policies.</b> The Seller at all time maintains
          commercially reasonable policies and procedures regarding privacy,
          data security, date breaches, data recovery, and destruction of
          business records with respect to all information it has, or had, in
          its possession related to the Purchased Loans and related Loan
          Obligors, including without limitation, loan applications, personally
          identifiable information, and other propriety and sensitive
          information.
        </Typography>

        <Typography className={classes.paragraph}>
          <b>k - Compliance with Anti-Money Laws.</b> The Seller complies with
          all applicable anti-money laundering laws and U.S. sanctions
          requirements, including without limitation, the Bank Secrecy Act, as
          amended by the USA PATRIOT Act of 2001, and the regulations of the
          U.S. Treasury Department’s Financial Crimes Enforcement Network and
          Office of Foreign Assets Control, including Executive Order 13224
          (collectively, the “Anti-Money Laundering Laws”); the Seller has
          implemented and maintains an anti-money laundering program in
          compliance with Anti-Money Laundering Laws; the Seller has conducted
          appropriate due diligence in connection with each Purchased Loan in
          compliance with Anti-Money Laundering Laws and determined (i) the
          legitimacy of the relevant Loan Obligor, including verification that
          the Loan Obligor is not the subject of any economic or trade sanctions
          maintained by the United States and that no Loan Obligor or guarantor
          on the Mortgage Loan is listed as a “specially designated national” or
          “blocked person” for purposes of the Anti-Money Laundering Laws; and
          (ii) that the funds used by such Mortgagor to purchase the Mortgaged
          Property on the origination date were disbursed from a U.S. bank
          account; and that the Seller maintains, and will retain for a period
          of five (5) years after the sale of each Purchased Loan, sufficient
          information to identify the relevant Loan Obligor for purposes of
          compliance with Anti-Money Laundering Laws, which information the Loan
          Obligor will make available to the Buyer upon request by the Buyer.
          With respect to a Purchased Loan sold by the Seller to the Buyer
          pursuant to this Agreement, in the event of a breach of a
          representation and warranty contained in Section 6 or this Section 7
          which would have an adverse effect on the Buyer or the Purchased Loan,
          (A) if the breach of representation or warranty is capable of cure,
          the Seller shall have five (5) Business Days from the receipt of
          notice of the breach to cure such breach; or (B) if (x) the Seller
          fails to cure such a breach within such time frame; or (y) if such a
          breach of representation or warranty is incapable of cure, then the
          Seller shall by no later than the seventh (7th) Business Day following
          receipt of notice of the breach, repurchase the Purchased Loan at a
          price to be agreed upon between Seller and Buyer.
        </Typography>
      </Box>
      <Typography className={classes.sectionHeader}>8. Repurchases.</Typography>
      <Box pl={3}>
        <Typography className={classes.paragraph}>
          a - Upon the repurchase by the Seller of a Purchased Loan pursuant to
          Section 7 hereof, the Buyer shall sell, transfer, assign, and
          otherwise convey to the Seller all right, title and interest of the
          Buyer in and to such Purchased Loan.
        </Typography>
        <Typography className={classes.paragraph}>
          b - Upon any repurchase of a Purchased Loan, the Buyer shall execute
          any documents, instruments, or assignments reasonably necessary to
          effect the transfer and assignment of all of the Buyer’s right, title
          and interest in and to such Purchased Loan to the Seller and shall
          release or terminate any liens then covering such Purchased Loan.
        </Typography>
      </Box>
      <Typography className={classes.sectionHeader}>
        9. Confidentiality and Publicity.
      </Typography>
      <Box pl={3}>
        <Typography className={classes.paragraph}>
          a - Except as expressly provided in this Section 9 or with the other
          Party’s prior written consent, both Parties agree to hold all
          Confidential Information of the other Party in confidence.
          Confidential Information shall be deemed the exclusive property of the
          disclosing Party. Neither Party, during the term and after termination
          of this Agreement, shall disclose any Confidential Information of the
          other Party to any person (other than its own directors, managers,
          officers, employees, agents and contractors who have a need to know
          such information for the performance of a Party’s obligations under
          this Agreement) or use any Confidential Information for purposes other
          than those contemplated by this Agreement, it being understood that
          (i) either Party hereto may make disclosures of the other Party’s
          Confidential Information (A) to Affiliates and to their agents and
          advisors provided that such Persons are informed of the confidential
          nature of the information and will be instructed to keep such
          information confidential; (B) to any rating agency when required by
          it, provided that, prior to any disclosure, such rating agency shall
          undertake in writing to preserve the confidentiality of any such
          confidential information received by it; or (C) as required or
          requested by any governmental authority or representative thereof or
          pursuant to legal or judicial process or other legal proceeding;
          provided, that unless specifically prohibited by Applicable Law or
          court order, the receiving Party shall make reasonable efforts to
          notify the disclosing Party of any request by any governmental
          authority or representative thereof (other than any such request in
          connection with any examination of the financial condition or other
          routine examination by such governmental authority) for disclosure of
          any Confidential Information prior to disclosure of such information;
          and (ii) the Buyer may make disclosures of the Seller’s Confidential
          Information (A) to any current or potential financing sources, buyers,
          investors, servicers, other business counterparties, or vendors of the
          Buyer; or (B) to potential purchasers of the Purchased Loans sold to
          the Buyer hereunder.
        </Typography>
        <Typography className={classes.paragraph}>
          b - Neither Party hereto shall issue any press releases or similar
          public announcements using the name of the other Party or referring to
          this Agreement or the transactions contemplated hereunder except (i)
          disclosures required by Applicable Law, legal process, or the rules of
          the Securities and Exchange Commission; or (ii) with the prior
          approval of the other Party.
        </Typography>
        <Typography className={classes.paragraph}>
          c - The Parties agree that monetary damages for breach of the
          obligations under this Section 9 may not be adequate and that the
          non-breaching Party shall be entitled to injunctive relief with
          respect to a breach hereof.
        </Typography>
      </Box>
      <Typography className={classes.sectionHeader}>
        10. Relationship of Parties.
      </Typography>
      <Typography className={classes.paragraph}>
        The Parties acknowledge and agree that this is an arms-length
        transaction, the relationship between them is solely that of independent
        contractors, and nothing in this Agreement is to be construed to
        constitute the Parties as employer/employee, franchiser/franchisee,
        agent/principal, partners, joint ventures, co-owners or otherwise as
        participants in a joint or common undertaking, nor to create a fiduciary
        duty between the Parties. Each Party shall bear its respective fees,
        costs, and expenses (including, but not limited to, attorney’s fees,
        broker’s fees, and accountant’s fees) incurred by it in connection with
        this Agreement and the transactions contemplated hereby, except as
        otherwise specifically set forth herein.
      </Typography>

      <Typography className={classes.sectionHeader}>11. Notices.</Typography>
      <Typography className={classes.paragraph}>
        Any notice under this Agreement shall be in writing and shall be sent by
        electronic mail and followed by certified or overnight mail. Such notice
        shall be deemed received on the actual date of delivery of the notice
        sent by electronic mail (assuming the sender has not received an
        electronic notice of non-delivery) to the following address, or such
        other address as a Party may subsequently designate in writing:
      </Typography>
      <Box pl={5}>
        <Typography className={classes.paragraph}>
          Seller: {lender.company.name}
          <Box pl={4}>
            &nbsp;{lender.company.address}<br />
            &nbsp;Attention: {`${fromValues.signatoryFirstName} ${fromValues.signatoryLastName}`}<br />
          </Box>

          Buyer: LOAN FUNDER LLC
          <Box pl={4}>
            &nbsp;c/o Roc Capital Holdings LLC 645<br />
            &nbsp;Madison Avenue, 19th Floor New York,<br />
            &nbsp;NY 10022<br />
            &nbsp;Attention: Maksim Stavinsky, COO E
          </Box>

        </Typography>
      </Box>

      <Typography className={classes.sectionHeader}>
        12. Modification of Agreement.
      </Typography>
      <Typography className={classes.paragraph}>
        This Agreement contains the entire agreement between the Parties
        relating to the Purchased Loans; there are no other agreements, express
        or implied, oral or written. Only an instrument in writing signed by
        each of the Parties hereto may modify this Agreement
      </Typography>

      <Typography className={classes.sectionHeader}>
        13. Governing Law; Submission to Jurisdiction; Waiver of Jury Trial.
      </Typography>
      <Typography className={classes.paragraph}>
        THIS AGREEMENT SHALL BE GOVERNED BY AND CONSTRUED IN ACCORDANCE WITH THE
        LAWS OF THE STATE OF NEW YORK, INCLUDING SECTIONS 5-1401 AND 5-1402 OF
        THE GENERAL OBLIGATIONS LAW, AND THE OBLIGATIONS, RIGHTS AND REMEDIES OF
        THE PARTIES HEREUNDER SHALL BE DETERMINED IN ACCORDANCE WITH SUCH LAWS
        WITHOUT GIVING EFFECT TO CONFLICT OF LAWS PRINCIPLES THEREOF.<br />
        <br />EACH PARTY EXPRESSLY AND IRREVOCABLY SUBMITS TO THE NONEXCLUSIVE JURISDICTION OF
        THE COURTS OF THE STATE OF NEW YORK LOCATED IN THE COUNTY OF NEW YORK
        AND OF THE UNITED STATES DISTRICT COURT FOR THE SOUTHERN DISTRICT OF NEW
        YORK FOR PURPOSES OF ALL LEGAL PROCEEDINGS ARISING OUT OF OR RELATING TO
        THIS AGREEMENT OR THE TRANSACTIONS CONTEMPLATED HEREBY. EACH PARTY
        IRREVOCABLY WAIVES, TO THE FULLEST EXTENT PERMITTED BY LAW, ANY
        OBJECTION WHICH IT MAY NOW OR HEREAFTER HAVE TO THE LAYING OF THE VENUE
        OF ANY SUCH PROCEEDING BROUGHT IN SUCH COURT AND ANY CLAIM THAT ANY SUCH
        PROCEEDING BROUGHT IN SUCH COURT HAS BEEN BROUGHT IN AN INCONVENIENT
        FORUM. EACH PARTY CONSENTS TO PROCESS BEING SERVED IN ANY SUIT, ACTION
        OR PROCEEDING WITH RESPECT TO THIS AGREEMENT, OR ANY DOCUMENT DELIVERED
        PURSUANT HERETO BY THE MAILING OF A COPY THEREOF BY REGISTERED OR
        CERTIFIED MAIL, POSTAGE PREPAID, RETURN RECEIPT REQUESTED, TO ITS
        RESPECTIVE ADDRESS SPECIFIED AT THE TIME FOR NOTICES UNDER THIS
        AGREEMENT.<br /> <br />
        EACH PARTY KNOWINGLY, VOLUNTARILY AND INTENTIONALLY, WAIVES
        (TO THE EXTENT PERMITTED BY APPLICABLE LAW) ANY RIGHT IT MAY HAVE TO A
        TRIAL BY JURY OF ANY DISPUTE ARISING UNDER OR RELATING TO THIS AGREEMENT
        AND AGREES THAT ANY SUCH DISPUTE SHALL BE TRIED BEFORE A JUDGE SITTING
        WITHOUT A JURY
      </Typography>

      <Typography className={classes.sectionHeader}>
        14. Indemnification by the Seller.
      </Typography>
      <Typography className={classes.paragraph}>
        The Seller shall indemnify the Buyer and hold the Buyer and each of the
        Buyer’s Representatives harmless against any and all claims, losses,
        damages, penalties, fines, forfeitures, reasonable and necessary legal
        fees and related costs, judgments, and any other costs, fees and
        expenses (collectively, the “Damages”) that the Buyer may sustain in any
        way related to the failure of the Seller to perform its obligations
        under this Agreement, including but not limited to, its obligation to
        originate the Purchased Loans in compliance with the terms of this
        Agreement. The Seller shall promptly pay or reimburse the Buyer for any
        Damages upon receipt of written notice from the Buyer or its designee.
        <br />
        <br />
        The Seller immediately shall notify the Buyer if a claim is made by a
        third party (including any mortgagor) with respect to this Agreement or
        any Purchased Loan, assume (with the prior written consent of the Buyer)
        the defense of any such claim, and pay all expenses in connection
        therewith, including reasonable counsel fees, and promptly pay,
        discharge and satisfy any judgment or decree which may be entered
        against it or the Buyer in respect of such claim. The Seller shall
        follow any commercially reasonable written instructions received from
        the Buyer in connection with any such claim.
      </Typography>

      <Typography className={classes.sectionHeader}>
        15. No Waivers; Remedies Cumulative; Survival; Counterparts.
      </Typography>
      <Typography className={classes.paragraph}>
        The waiver of any breach of this Agreement shall not be construed to be
        a waiver of any other or subsequent breach. All remedies afforded by
        this Agreement for a breach hereof shall be cumulative; that is, in
        addition to all other remedies provided for herein or by law or in
        equity. The provisions of Sections 6, 7, 8, 9, 10, 11, 12, 13, 14 and 16
        shall survive the sale and transfer of Purchased Loans from the Seller
        to the Buyer.
        <br />
        <br />
        This Agreement may be executed in any number of counterparts. Each
        counterpart shall be deemed to be an original and all such counterparts
        shall constitute one and the same instrument. An emailed signed copy of
        this Agreement shall be deemed the same as an original and legally
        binding.
      </Typography>

      <Typography className={classes.sectionHeader}>
        16. Severability.
      </Typography>
      <Typography className={classes.paragraph}>
        If any provision of this Agreement shall be determined to be invalid,
        illegal, or unenforceable by a court, the balance of this Agreement
        shall remain in full force and effect; and if any provision is
        inapplicable to any person or circumstance, it shall nonetheless remain
        applicable to all other Persons and circumstances.
      </Typography>

      <Typography className={classes.sectionHeader}>
        17. Previously Purchased Loans.
      </Typography>
      <Typography className={classes.paragraph}>
        The Seller and the Buyer each hereby acknowledge and agree that each of
        the terms and provisions hereof, including, without limitation, all
        covenants and representations and warranties, shall also be applicable
        to any and all Previously Purchased Loans.
      </Typography>

      <Typography className={classes.title}>
        <br />
        EXHIBIT A LOAN DOCUMENTS
      </Typography>
      <Typography className={classes.sectionHeader}>
        FOR EACH PURCHASED LOAN:
      </Typography>
      <Typography className={classes.paragraph}>
        The original Note, allonge, together with any applicable riders, endorsed in blank, with all prior
        intervening endorsements as may be necessary (if applicable) to show a complete endorsement
        chain
        <br /><br />
        The original Mortgage, or the electronic recording thereof, with any applicable riders thereto, or
        if the original Mortgage has been submitted for recordation but has not been returned from the
        applicable public recording office, a certified copy of the Mortgage
        <br /><br />
        An original Assignment of Mortgage in recordable form, assigned to blank
        <br /><br />
        Each intervening Assignment of Mortgage, if any, to show a complete chain of title from the
        originating person to the Seller
        <br /><br />
        The original policy of title insurance (or the original commitment of title insurance if the title
        insurance policy has not yet been returned prior to delivery)
        <br /><br />
        Copies of any loan modification or extension agreement(s)
        <br /><br />
        The Assignment of Leases and Rents (if separate from the Mortgage)
        <br /><br />
        An original Assignment of Assignment of Leases and Rents in recordable form, assigned to blank
        (if applicable)
        <br /><br />
        The Assignment of Permits and Contracts, if any
        <br /><br />
        The Environmental Indemnity Agreement
        <br /><br />
        The Collateral Assignment and Security Agreement (or equivalent document), if any
        <br /><br />
        The Guaranty
        <br /><br />
        The original or copy of the Power of Attorney if the Mortgage or Note was executed by a power of
        attorney
      </Typography>

      <Typography className={classes.title}>
        <br />
        EXHIBIT B
      </Typography>
      <Typography className={classes.title}>
        FORM OF ASSIGNMENT & CONVEYANCE
      </Typography>
      <Typography className={classes.paragraph}>
        &nbsp;On this {format(new Date(), "d 'day of' MMMM, yyyy")}, <b>{lender.company.name},</b>
        <br /><br />
        (the “Seller”), under that certain Master Loan Purchase and Sale Agreement, dated as of [DATE]
        (the “<b>Agreement”</b>), does hereby sell, transfer, assign, set over and convey to LOAN FUNDER
        LLC, as buyer (the “<b>Buyer</b>”), under the Agreement, without recourse, all rights, title, and interest
        of the Seller in and to the Purchased Loan(s) listed on the Schedule of Purchased Loans attached
        hereto as Annex A, including, without limitation, all Loan Documents and all other agreements,
        certificates, or other documents now or hereafter evidencing, guaranteeing, securing, or otherwise
        executed in connection with such Purchased Loan(s).
        <br /><br />
        The Seller confirms to the Buyer that the representation and warranties set forth in
        Sections 6 and 7 of the Agreement are true and correct with respect to the Seller and the
        Purchased Loan(s) listed on the Schedule of Underlying Loans attached hereto as of the date
        hereof.
        <br /><br />
        The ownership of each Purchased Loan is vested in the Buyer.
        <br /><br />
        Capitalized terms used herein and not otherwise defined shall have the meanings
        set forth in the Agreement.
      </Typography>
      <SignatureField ref={signatureRef} penColor="green" />
    </Box>
  );
});
